import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { updateProfile } from '../api/user'
import { getAccessToken } from '../lib/session'
import { de } from './de'
import { en } from './en'
import { es } from './es'
import { fr } from './fr'
import { it } from './it'
import { ka } from './ka'
import { th } from './th'
import { zh } from './zh'
import { pt } from './pt'
import { ro } from './ro'
import { ru } from './ru'
import { isLocale, profileLocaleOf, type Locale, type Translations } from './types'

export { LOCALES, LOCALE_NAMES, LOCALE_TAGS, isLocale, profileLocaleOf } from './types'
export type { Locale, Translations } from './types'

const DICTIONARIES: Record<Locale, Translations> = { ru, en, ka, fr, it, ro, pt, es, th, zh, de }
const STORAGE_KEY = 'locale'

/** The language picked on this device, if it was ever picked here. */
function storedLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return isLocale(stored) ? stored : null
  } catch {
    // Private mode and blocked site data both throw here.
    return null
  }
}

/** Stored choice, else the browser's preference, else Russian. */
function detectLocale(): Locale {
  const stored = storedLocale()
  if (stored !== null) return stored

  for (const candidate of navigator.languages ?? []) {
    const short = candidate.slice(0, 2).toLowerCase()
    if (isLocale(short)) return short
  }
  return 'ru'
}

interface I18nValue {
  locale: Locale
  t: Translations
  setLocale: (locale: Locale) => void
  /** True when the profile language now governs this device; false when the screen's own language should. */
  adoptProfileLocale: (language: unknown) => boolean
}

const I18nContext = createContext<I18nValue | null>(null)

export function I18nProvider({ children }: { children: ReactNode }): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(detectLocale)

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Persisting the choice is a convenience, never a requirement.
    }
    // The profile remembers the language too, so emails and push can follow it
    // and a new device starts in the right one (see `adoptProfileLocale`). A
    // failure here changes nothing on screen: the choice already applied.
    if (getAccessToken() !== null) {
      void updateProfile({ language: next }).catch(() => undefined)
    }
  }, [])

  /*
   * The other half of the promise above: a device on which no language was
   * ever picked follows the profile once it loads, so the screen speaks the
   * same language as the emails and the push. An explicit choice made here
   * stays; the profile is not re-posted, and nothing is stored, so a change
   * made on another device is picked up on the next load. Returns whether the
   * profile took charge — when it did not (a choice was made here, or the
   * profile names a language the app does not ship), the caller brings the
   * profile in line with the screen instead.
   */
  const adoptProfileLocale = useCallback((language: unknown): boolean => {
    if (storedLocale() !== null) return false
    const next = profileLocaleOf(language)
    if (next === null) return false
    setLocaleState(next)
    return true
  }, [])

  const value = useMemo<I18nValue>(
    () => ({ locale, t: DICTIONARIES[locale], setLocale, adoptProfileLocale }),
    [locale, setLocale, adoptProfileLocale],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

function useI18n(): I18nValue {
  const value = useContext(I18nContext)
  if (!value) throw new Error('useI18n must be used inside <I18nProvider>')
  return value
}

/** `const t = useT()` then `t.wallet.deposit.title` — typed all the way down. */
export function useT(): Translations {
  return useI18n().t
}

export function useLocale(): { locale: Locale; setLocale: (locale: Locale) => void } {
  const { locale, setLocale } = useI18n()
  return { locale, setLocale }
}

/** For the session: the profile's language, applied where the device has no choice of its own. */
export function useAdoptProfileLocale(): (language: unknown) => boolean {
  return useI18n().adoptProfileLocale
}
